/**
 * Flag Hook
 *
 * Provides flag components for language codes and country codes
 * Uses nucleo-flags package for SVG flag icons
 */

import { useMemo } from "react";
import type { FC } from "react";
import type { IconProps } from "nucleo-flags";
import {
  IconGermany,
  IconUnitedKingdom,
  IconNetherlands,
  IconTurkey,
  IconSaudiArabia,
  IconFrance,
  IconSpain,
  IconItaly,
  IconPoland,
  IconRussia,
  IconChina,
  IconJapan,
  IconPortugal,
  IconBrazil,
  IconMexico,
  IconArgentina,
  IconChile,
  IconColombia,
  IconPeru,
  IconVenezuela,
  IconEcuador,
  IconBolivia,
  IconParaguay,
  IconUruguay,
  IconCostaRica,
  IconPanama,
  IconCuba,
  IconDominicanRepublic,
  IconGuatemala,
  IconHonduras,
  IconElSalvador,
  IconNicaragua,
  IconPuertoRico,
  IconBelize,
  IconJamaica,
  IconTrinidadTobago,
  IconBahamas,
  IconBarbados,
  IconHaiti,
  IconGuyana,
  IconSuriname,
  IconFrenchGuiana,
  IconMartinique,
  IconGuadeloupe,
  IconGreece,
  IconRomania,
  IconBulgaria,
  IconCroatia,
  IconSerbia,
  IconSlovenia,
  IconBosniaHerzegovina,
  IconNorthMacedonia,
  IconAlbania,
  IconMontenegro,
  IconKosovo,
  IconCzechia,
  IconSlovakia,
  IconHungary,
  IconAustria,
  IconSwitzerland,
  IconBelgium,
  IconLuxembourg,
  IconLiechtenstein,
  IconMonaco,
  IconAndorra,
  IconSanMarino,
  IconVaticanCity,
  IconMalta,
  IconCyprus,
  IconIreland,
  IconIceland,
  IconNorway,
  IconSweden,
  IconFinland,
  IconDenmark,
  IconEstonia,
  IconLatvia,
  IconLithuania,
  IconBelarus,
  IconUkraine,
  IconMoldova,
  IconGeorgia,
  IconArmenia,
  IconAzerbaijan,
  IconKazakhstan,
  IconUzbekistan,
  IconTurkmenistan,
  IconKyrgyzstan,
  IconTajikistan,
  IconAfghanistan,
  IconPakistan,
  IconIndia,
  IconBangladesh,
  IconSriLanka,
  IconNepal,
  IconBhutan,
  IconMaldives,
  IconMyanmar,
  IconThailand,
  IconLaos,
  IconCambodia,
  IconVietnam,
  IconMalaysia,
  IconSingapore,
  IconIndonesia,
  IconPhilippines,
  IconBrunei,
  IconEastTimor,
  IconSouthKorea,
  IconNorthKorea,
  IconMongolia,
  IconHongKong,
  IconMacau,
  IconTaiwan,
  IconIran,
  IconIraq,
  IconIsrael,
  IconJordan,
  IconLebanon,
  IconSyria,
  IconYemen,
  IconOman,
  IconUnitedArabEmirates,
  IconKuwait,
  IconQatar,
  IconBahrain,
  IconEgypt,
  IconLibya,
  IconTunisia,
  IconAlgeria,
  IconMorocco,
  IconMauritania,
  IconMali,
  IconNiger,
  IconChad,
  IconSudan,
  IconSouthSudan,
  IconEthiopia,
  IconEritrea,
  IconDjibouti,
  IconSomalia,
  IconKenya,
  IconUganda,
  IconTanzania,
  IconRwanda,
  IconBurundi,
  IconSeychelles,
  IconMauritius,
  IconComoros,
  IconMadagascar,
  IconMozanbique,
  IconZambia,
  IconZimbabwe,
  IconMalawi,
  IconBotswana,
  IconNamibia,
  IconSouthAfrica,
  IconLesotho,
  IconEswatini,
  IconAngola,
  IconDemocraticRepublicCongo,
  IconRepublicCongo,
  IconGabon,
  IconEquatorialGuinea,
  IconCameroon,
  IconCentralAfricanRepublic,
  IconNigeria,
  IconBenin,
  IconTogo,
  IconGhana,
  IconIvoryCoast,
  IconBurkinaFaso,
  IconLiberia,
  IconSierraLeone,
  IconGuinea,
  IconGuineaBissau,
  IconSenegal,
  IconGambia,
  IconCapeVerde,
  IconSaoTomePrincipe,
  IconCanada,
  IconUnitedStates,
  IconAustralia,
  IconNewZealand,
  IconPapuaNewGuinea,
  IconFiji,
  IconSolomonIslands,
  IconVanuatu,
  IconSamoa,
  IconTonga,
  IconKiribati,
  IconTuvalu,
  IconNauru,
  IconPalau,
  IconMicronesia,
  IconMarshallIslands,
  IconCookIslands,
  IconNiue,
  IconFrenchPolynesia,
  IconNewCaledonia,
  IconGreenland,
  IconFaroeIslands,
  IconAalandIslands,
  IconAntarctica,
  IconFalklandIslands,
  IconBermuda,
  IconCaymanIslands,
  IconBritishVirginIslands,
  IconTurksAndCaicosIslands,
  IconAnguilla,
  IconGibraltar,
  IconGuernsey,
  IconJersey,
  IconIsleOfMan,
  IconAruba,
  IconCuracao,
  IconSintMaarten,
  IconUnitedStatesVirginIslands,
  IconGrenada,
  IconSaintLucia,
  IconSaintVincentGrenadines,
  IconDominica,
  IconSaintKittsNevis,
  IconAntiguaBarbuda,
} from "nucleo-flags";

// Language code to country code mapping (ISO 639-1 to ISO 3166-1 alpha-2)
const LANGUAGE_TO_COUNTRY: Record<string, string> = {
  // Germanic languages
  de: "DE", // German -> Germany
  en: "GB", // English -> United Kingdom
  nl: "NL", // Dutch -> Netherlands
  da: "DK", // Danish -> Denmark
  no: "NO", // Norwegian -> Norway
  sv: "SE", // Swedish -> Sweden
  is: "IS", // Icelandic -> Iceland
  lb: "LU", // Luxembourgish -> Luxembourg

  // Romance languages
  fr: "FR", // French -> France
  es: "ES", // Spanish -> Spain
  it: "IT", // Italian -> Italy
  pt: "PT", // Portuguese -> Portugal
  ro: "RO", // Romanian -> Romania
  ca: "ES", // Catalan -> Spain
  gl: "ES", // Galician -> Spain

  // Slavic languages
  pl: "PL", // Polish -> Poland
  ru: "RU", // Russian -> Russia
  uk: "UA", // Ukrainian -> Ukraine
  cs: "CZ", // Czech -> Czechia
  sk: "SK", // Slovak -> Slovakia
  hr: "HR", // Croatian -> Croatia
  sr: "RS", // Serbian -> Serbia
  sl: "SI", // Slovenian -> Slovenia
  bg: "BG", // Bulgarian -> Bulgaria
  mk: "MK", // Macedonian -> North Macedonia
  be: "BY", // Belarusian -> Belarus
  bs: "BA", // Bosnian -> Bosnia and Herzegovina

  // Turkic languages
  tr: "TR", // Turkish -> Turkey
  az: "AZ", // Azerbaijani -> Azerbaijan
  kk: "KZ", // Kazakh -> Kazakhstan
  uz: "UZ", // Uzbek -> Uzbekistan
  tk: "TM", // Turkmen -> Turkmenistan
  ky: "KG", // Kyrgyz -> Kyrgyzstan

  // Semitic languages
  ar: "SA", // Arabic -> Saudi Arabia
  he: "IL", // Hebrew -> Israel

  // Indo-Aryan languages
  hi: "IN", // Hindi -> India
  bn: "BD", // Bengali -> Bangladesh
  ur: "PK", // Urdu -> Pakistan
  ne: "NP", // Nepali -> Nepal
  si: "LK", // Sinhala -> Sri Lanka

  // Iranian languages
  fa: "IR", // Persian -> Iran
  ps: "AF", // Pashto -> Afghanistan
  tg: "TJ", // Tajik -> Tajikistan
  ku: "IQ", // Kurdish -> Iraq

  // East Asian languages
  zh: "CN", // Chinese -> China
  ja: "JP", // Japanese -> Japan
  ko: "KR", // Korean -> South Korea

  // Southeast Asian languages
  th: "TH", // Thai -> Thailand
  vi: "VN", // Vietnamese -> Vietnam
  my: "MM", // Burmese -> Myanmar
  lo: "LA", // Lao -> Laos
  km: "KH", // Khmer -> Cambodia
  ms: "MY", // Malay -> Malaysia
  id: "ID", // Indonesian -> Indonesia
  tl: "PH", // Tagalog -> Philippines

  // Other European languages
  el: "GR", // Greek -> Greece
  hu: "HU", // Hungarian -> Hungary
  fi: "FI", // Finnish -> Finland
  et: "EE", // Estonian -> Estonia
  lv: "LV", // Latvian -> Latvia
  lt: "LT", // Lithuanian -> Lithuania
  ga: "IE", // Irish -> Ireland
  mt: "MT", // Maltese -> Malta
  sq: "AL", // Albanian -> Albania
  hy: "AM", // Armenian -> Armenia
  ka: "GE", // Georgian -> Georgia

  // African languages
  sw: "KE", // Swahili -> Kenya
  am: "ET", // Amharic -> Ethiopia
  ha: "NG", // Hausa -> Nigeria
  yo: "NG", // Yoruba -> Nigeria
  zu: "ZA", // Zulu -> South Africa
  xh: "ZA", // Xhosa -> South Africa
  af: "ZA", // Afrikaans -> South Africa
  so: "SO", // Somali -> Somalia
};

// Country code to flag component mapping (ISO 3166-1 alpha-2)
const COUNTRY_TO_FLAG: Record<string, FC<IconProps>> = {
  // Europe
  DE: IconGermany,
  GB: IconUnitedKingdom,
  UK: IconUnitedKingdom, // Alternative for GB
  NL: IconNetherlands,
  FR: IconFrance,
  ES: IconSpain,
  IT: IconItaly,
  PT: IconPortugal,
  BE: IconBelgium,
  AT: IconAustria,
  CH: IconSwitzerland,
  LU: IconLuxembourg,
  IE: IconIreland,
  DK: IconDenmark,
  SE: IconSweden,
  NO: IconNorway,
  FI: IconFinland,
  IS: IconIceland,
  GR: IconGreece,
  PL: IconPoland,
  CZ: IconCzechia,
  SK: IconSlovakia,
  HU: IconHungary,
  RO: IconRomania,
  BG: IconBulgaria,
  HR: IconCroatia,
  SI: IconSlovenia,
  EE: IconEstonia,
  LV: IconLatvia,
  LT: IconLithuania,
  MT: IconMalta,
  CY: IconCyprus,
  RS: IconSerbia,
  BA: IconBosniaHerzegovina,
  MK: IconNorthMacedonia,
  AL: IconAlbania,
  ME: IconMontenegro,
  XK: IconKosovo,
  UA: IconUkraine,
  BY: IconBelarus,
  MD: IconMoldova,
  LI: IconLiechtenstein,
  MC: IconMonaco,
  AD: IconAndorra,
  SM: IconSanMarino,
  VA: IconVaticanCity,

  // Asia
  TR: IconTurkey,
  RU: IconRussia,
  CN: IconChina,
  JP: IconJapan,
  KR: IconSouthKorea,
  KP: IconNorthKorea,
  IN: IconIndia,
  PK: IconPakistan,
  BD: IconBangladesh,
  LK: IconSriLanka,
  NP: IconNepal,
  BT: IconBhutan,
  MV: IconMaldives,
  TH: IconThailand,
  VN: IconVietnam,
  MY: IconMalaysia,
  SG: IconSingapore,
  ID: IconIndonesia,
  PH: IconPhilippines,
  MM: IconMyanmar,
  KH: IconCambodia,
  LA: IconLaos,
  BN: IconBrunei,
  TL: IconEastTimor,
  MN: IconMongolia,
  HK: IconHongKong,
  MO: IconMacau,
  TW: IconTaiwan,
  SA: IconSaudiArabia,
  AE: IconUnitedArabEmirates,
  IL: IconIsrael,
  JO: IconJordan,
  LB: IconLebanon,
  SY: IconSyria,
  IQ: IconIraq,
  IR: IconIran,
  YE: IconYemen,
  OM: IconOman,
  KW: IconKuwait,
  QA: IconQatar,
  BH: IconBahrain,
  GE: IconGeorgia,
  AM: IconArmenia,
  AZ: IconAzerbaijan,
  KZ: IconKazakhstan,
  UZ: IconUzbekistan,
  TM: IconTurkmenistan,
  KG: IconKyrgyzstan,
  TJ: IconTajikistan,
  AF: IconAfghanistan,

  // Africa
  EG: IconEgypt,
  LY: IconLibya,
  TN: IconTunisia,
  DZ: IconAlgeria,
  MA: IconMorocco,
  MR: IconMauritania,
  ML: IconMali,
  NE: IconNiger,
  TD: IconChad,
  SD: IconSudan,
  SS: IconSouthSudan,
  ET: IconEthiopia,
  ER: IconEritrea,
  DJ: IconDjibouti,
  SO: IconSomalia,
  KE: IconKenya,
  UG: IconUganda,
  TZ: IconTanzania,
  RW: IconRwanda,
  BI: IconBurundi,
  SC: IconSeychelles,
  MU: IconMauritius,
  KM: IconComoros,
  MG: IconMadagascar,
  MZ: IconMozanbique,
  ZM: IconZambia,
  ZW: IconZimbabwe,
  MW: IconMalawi,
  BW: IconBotswana,
  NA: IconNamibia,
  ZA: IconSouthAfrica,
  LS: IconLesotho,
  SZ: IconEswatini,
  AO: IconAngola,
  CD: IconDemocraticRepublicCongo,
  CG: IconRepublicCongo,
  GA: IconGabon,
  GQ: IconEquatorialGuinea,
  CM: IconCameroon,
  CF: IconCentralAfricanRepublic,
  NG: IconNigeria,
  BJ: IconBenin,
  TG: IconTogo,
  GH: IconGhana,
  CI: IconIvoryCoast,
  BF: IconBurkinaFaso,
  LR: IconLiberia,
  SL: IconSierraLeone,
  GN: IconGuinea,
  GW: IconGuineaBissau,
  SN: IconSenegal,
  GM: IconGambia,
  CV: IconCapeVerde,
  ST: IconSaoTomePrincipe,

  // Americas
  US: IconUnitedStates,
  CA: IconCanada,
  MX: IconMexico,
  BR: IconBrazil,
  AR: IconArgentina,
  CL: IconChile,
  CO: IconColombia,
  PE: IconPeru,
  VE: IconVenezuela,
  EC: IconEcuador,
  BO: IconBolivia,
  PY: IconParaguay,
  UY: IconUruguay,
  CR: IconCostaRica,
  PA: IconPanama,
  CU: IconCuba,
  DO: IconDominicanRepublic,
  GT: IconGuatemala,
  HN: IconHonduras,
  SV: IconElSalvador,
  NI: IconNicaragua,
  PR: IconPuertoRico,
  BZ: IconBelize,
  JM: IconJamaica,
  TT: IconTrinidadTobago,
  BS: IconBahamas,
  BB: IconBarbados,
  HT: IconHaiti,
  GY: IconGuyana,
  SR: IconSuriname,
  GF: IconFrenchGuiana,
  MQ: IconMartinique,
  GP: IconGuadeloupe,
  GD: IconGrenada,
  LC: IconSaintLucia,
  VC: IconSaintVincentGrenadines,
  DM: IconDominica,
  KN: IconSaintKittsNevis,
  AG: IconAntiguaBarbuda,

  // Oceania
  AU: IconAustralia,
  NZ: IconNewZealand,
  PG: IconPapuaNewGuinea,
  FJ: IconFiji,
  SB: IconSolomonIslands,
  VU: IconVanuatu,
  WS: IconSamoa,
  TO: IconTonga,
  KI: IconKiribati,
  TV: IconTuvalu,
  NR: IconNauru,
  PW: IconPalau,
  FM: IconMicronesia,
  MH: IconMarshallIslands,
  CK: IconCookIslands,
  NU: IconNiue,
  PF: IconFrenchPolynesia,
  NC: IconNewCaledonia,

  // Territories
  GL: IconGreenland,
  FO: IconFaroeIslands,
  AX: IconAalandIslands,
  AQ: IconAntarctica,
  FK: IconFalklandIslands,
  BM: IconBermuda,
  KY: IconCaymanIslands,
  VG: IconBritishVirginIslands,
  TC: IconTurksAndCaicosIslands,
  AI: IconAnguilla,
  GI: IconGibraltar,
  GG: IconGuernsey,
  JE: IconJersey,
  IM: IconIsleOfMan,
  AW: IconAruba,
  CW: IconCuracao,
  SX: IconSintMaarten,
  VI: IconUnitedStatesVirginIslands,
};

export interface UseFlagsResult {
  /** Get flag component by language code (ISO 639-1) */
  getFlagByLanguage: (languageCode: string) => FC<IconProps> | undefined;
  /** Get flag component by country code (ISO 3166-1 alpha-2) */
  getFlagByCountry: (countryCode: string) => FC<IconProps> | undefined;
  /** Get flag component by either language or country code */
  getFlag: (code: string) => FC<IconProps> | undefined;
}

/**
 * Hook to get flag components for language codes and country codes
 *
 * @example
 * ```tsx
 * import { useFlags } from "@ascleo/icons/hooks";
 *
 * function MyComponent() {
 *   const { getFlagByLanguage, getFlagByCountry } = useFlags();
 *
 *   const GermanFlag = getFlagByLanguage("de"); // Returns IconGermany
 *   const USFlag = getFlagByCountry("US"); // Returns IconUnitedStates
 *
 *   return (
 *     <div>
 *       {GermanFlag && <GermanFlag size={24} />}
 *       {USFlag && <USFlag size={24} />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useFlags(): UseFlagsResult {
  const getFlagByLanguage = useMemo(
    () => (languageCode: string) => {
      const normalizedCode = languageCode.toLowerCase();
      const countryCode = LANGUAGE_TO_COUNTRY[normalizedCode];
      if (!countryCode) return undefined;
      return COUNTRY_TO_FLAG[countryCode.toUpperCase()];
    },
    []
  );

  const getFlagByCountry = useMemo(
    () => (countryCode: string) => {
      const normalizedCode = countryCode.toUpperCase();
      return COUNTRY_TO_FLAG[normalizedCode];
    },
    []
  );

  const getFlag = useMemo(
    () => (code: string) => {
      // Try as country code first (2 uppercase letters)
      if (code.length === 2 && code === code.toUpperCase()) {
        const flag = getFlagByCountry(code);
        if (flag) return flag;
      }
      // Try as language code (2 lowercase letters)
      if (code.length === 2 && code === code.toLowerCase()) {
        return getFlagByLanguage(code);
      }
      // Try both
      return getFlagByCountry(code) || getFlagByLanguage(code);
    },
    [getFlagByCountry, getFlagByLanguage]
  );

  return {
    getFlagByLanguage,
    getFlagByCountry,
    getFlag,
  };
}
